import { jsPDF } from "jspdf";

export interface VolanteOptions {
  lotteryName: string;
  universeMin: number;
  universeMax: number;
  pickCount: number;
  cols: number; // colunas da grade
  games: number[][];
  brandLine?: string;
  costPerGame?: number;
}

const PRIMARY: [number, number, number] = [201, 168, 76]; // gold
const DARK: [number, number, number] = [16, 24, 41];
const LIGHT_GRID: [number, number, number] = [220, 220, 220];

// ============================================================
// MODO 1 — Lista compacta (só números, vários jogos por página)
// ============================================================
export interface ListaOptions {
  lotteryName: string;
  contestRef?: string;
  games: number[][];
  costPerGame?: number;
  brandLine?: string;
}

export function generateGameListPDF(opts: ListaOptions): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const headerH = 20;
  const rowH = 8;
  const rowsPerPage = Math.floor((pageH - margin * 2 - headerH) / rowH);

  const drawHeader = (pageNum: number, totalPages: number) => {
    doc.setFillColor(...DARK);
    doc.rect(margin, margin, pageW - margin * 2, headerH, "F");
    doc.setTextColor(...PRIMARY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(opts.lotteryName.toUpperCase(), margin + 4, margin + 8);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      `${opts.games.length} jogo${opts.games.length !== 1 ? "s" : ""}` +
        (opts.contestRef ? ` • ${opts.contestRef}` : ""),
      margin + 4,
      margin + 14,
    );
    doc.setTextColor(...PRIMARY);
    doc.setFontSize(7);
    doc.text(opts.brandLine ?? "LotoTerminal", pageW - margin - 4, margin + 14, { align: "right" });
    doc.text(`Página ${pageNum}/${totalPages}`, pageW - margin - 4, margin + 8, { align: "right" });
  };

  const totalPages = Math.max(1, Math.ceil(opts.games.length / rowsPerPage));

  opts.games.forEach((game, idx) => {
    const slot = idx % rowsPerPage;
    if (idx > 0 && slot === 0) doc.addPage();
    if (slot === 0) drawHeader(Math.floor(idx / rowsPerPage) + 1, totalPages);

    const y = margin + headerH + 6 + slot * rowH;
    // zebra
    if (slot % 2 === 1) {
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y - 5, pageW - margin * 2, rowH, "F");
    }
    doc.setTextColor(120, 120, 120);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`#${String(idx + 1).padStart(3, "0")}`, margin + 2, y);

    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const numbers = game.map((n) => String(n).padStart(2, "0")).join("  ");
    doc.text(numbers, margin + 18, y);

    if (opts.costPerGame) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `R$ ${opts.costPerGame.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        pageW - margin - 2,
        y,
        { align: "right" },
      );
    }
  });

  return doc;
}

export function buildGameListPrintHtml(opts: ListaOptions): string {
  const rows = opts.games
    .map((g, i) => {
      const nums = g.map((n) => `<span class="num">${String(n).padStart(2, "0")}</span>`).join("");
      const cost = opts.costPerGame
        ? `<td class="cost">R$ ${opts.costPerGame.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>`
        : "";
      return `<tr><td class="idx">#${String(i + 1).padStart(3, "0")}</td><td class="nums">${nums}</td>${cost}</tr>`;
    })
    .join("");
  const total = opts.costPerGame ? opts.costPerGame * opts.games.length : 0;
  const totalRow = opts.costPerGame
    ? `<tfoot><tr><td colspan="2" class="total-lbl">Total (${opts.games.length} jogos)</td><td class="cost total">R$ ${total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr></tfoot>`
    : "";
  return `<!doctype html><html><head><meta charset="utf-8"/><title>${opts.lotteryName} — ${opts.games.length} jogos</title>
<style>
@page{size:A4 portrait;margin:14mm}
html,body{margin:0;padding:0;background:white;font-family:Helvetica,Arial,sans-serif;color:#101829}
header{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #101829;padding-bottom:8px;margin-bottom:10px}
h1{margin:0;font-size:18px;letter-spacing:.5px}
.sub{color:#6b6b6b;font-size:11px;margin-top:2px}
.brand{color:#C9A84C;font-weight:bold;font-size:12px}
table{width:100%;border-collapse:collapse;font-size:11px}
tbody tr:nth-child(even){background:#f5f5f5}
td{padding:6px 4px;vertical-align:middle}
.idx{color:#6b6b6b;width:38px}
.nums{font-weight:bold;letter-spacing:1px}
.num{display:inline-block;min-width:22px;text-align:center}
.cost{text-align:right;color:#6b6b6b;width:80px}
.total-lbl{text-align:right;font-weight:bold;padding-top:10px;border-top:1px solid #101829}
.total{font-weight:bold;color:#101829;padding-top:10px;border-top:1px solid #101829}
@media screen{body{background:#f5f5f5}main{max-width:780px;margin:20px auto;padding:14mm;background:white;box-shadow:0 0 0 1px #ddd}}
</style></head><body><main>
<header><div><h1>${opts.lotteryName.toUpperCase()}</h1><div class="sub">${opts.games.length} jogo${opts.games.length !== 1 ? "s" : ""}${opts.contestRef ? ` • ${opts.contestRef}` : ""}</div></div><div class="brand">${opts.brandLine ?? "LotoTerminal"}</div></header>
<table><tbody>${rows}</tbody>${totalRow}</table>
</main><script>window.addEventListener('load',()=>setTimeout(()=>{window.focus();window.print();},120));</script></body></html>`;
}

// ============================================================
// MODO 1b — Lista compacta agrupada por loteria
// ============================================================
export interface ListaGroup {
  lotteryName: string;
  games: number[][];
  costPerGame?: number;
}
export interface ListaGroupedOptions {
  groups: ListaGroup[];
  brandLine?: string;
}

export function generateGameListGroupedPDF(opts: ListaGroupedOptions): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const headerH = 20;
  const rowH = 8;
  const sectionGap = 6;

  const totalGames = opts.groups.reduce((a, g) => a + g.games.length, 0);
  const brand = opts.brandLine ?? "LotoTerminal";

  const drawHeader = () => {
    doc.setFillColor(...DARK);
    doc.rect(margin, margin, pageW - margin * 2, headerH, "F");
    doc.setTextColor(...PRIMARY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(`MEUS JOGOS — ${totalGames} jogo${totalGames !== 1 ? "s" : ""}`, margin + 4, margin + 8);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${opts.groups.length} loteria(s)`, margin + 4, margin + 14);
    doc.setTextColor(...PRIMARY);
    doc.setFontSize(7);
    doc.text(brand, pageW - margin - 4, margin + 14, { align: "right" });
  };

  let y = margin + headerH + 6;
  drawHeader();

  const ensureSpace = (needed: number) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      drawHeader();
      y = margin + headerH + 6;
    }
  };

  opts.groups.forEach((group, gIdx) => {
    if (gIdx > 0) y += sectionGap;
    ensureSpace(rowH + 4);
    // section title
    doc.setFillColor(245, 240, 220);
    doc.rect(margin, y - 5, pageW - margin * 2, rowH, "F");
    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(
      `${group.lotteryName.toUpperCase()} — ${group.games.length} jogo${group.games.length !== 1 ? "s" : ""}`,
      margin + 2,
      y,
    );
    y += rowH;

    group.games.forEach((game, idx) => {
      ensureSpace(rowH);
      if (idx % 2 === 1) {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y - 5, pageW - margin * 2, rowH, "F");
      }
      doc.setTextColor(120, 120, 120);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(`#${String(idx + 1).padStart(3, "0")}`, margin + 2, y);

      doc.setTextColor(...DARK);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const numbers = game.map((n) => String(n).padStart(2, "0")).join("  ");
      doc.text(numbers, margin + 18, y);

      if (group.costPerGame) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `R$ ${group.costPerGame.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          pageW - margin - 2,
          y,
          { align: "right" },
        );
      }
      y += rowH;
    });
  });

  return doc;
}

export function buildGameListGroupedPrintHtml(opts: ListaGroupedOptions): string {
  const totalGames = opts.groups.reduce((a, g) => a + g.games.length, 0);
  const sections = opts.groups
    .map((g) => {
      const rows = g.games
        .map((game, i) => {
          const nums = game.map((n) => `<span class="num">${String(n).padStart(2, "0")}</span>`).join("");
          const cost = g.costPerGame
            ? `<td class="cost">R$ ${g.costPerGame.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>`
            : "";
          return `<tr><td class="idx">#${String(i + 1).padStart(3, "0")}</td><td class="nums">${nums}</td>${cost}</tr>`;
        })
        .join("");
      const total = g.costPerGame ? g.costPerGame * g.games.length : 0;
      const totalRow = g.costPerGame
        ? `<tfoot><tr><td colspan="2" class="total-lbl">Subtotal (${g.games.length} jogos)</td><td class="cost total">R$ ${total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr></tfoot>`
        : "";
      return `<section class="grp"><h2>${g.lotteryName.toUpperCase()} — ${g.games.length} jogo${g.games.length !== 1 ? "s" : ""}</h2>
<table><tbody>${rows}</tbody>${totalRow}</table></section>`;
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Meus jogos — ${totalGames} jogos</title>
<style>
@page{size:A4 portrait;margin:14mm}
html,body{margin:0;padding:0;background:white;font-family:Helvetica,Arial,sans-serif;color:#101829}
header{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #101829;padding-bottom:8px;margin-bottom:10px}
h1{margin:0;font-size:18px;letter-spacing:.5px}
.sub{color:#6b6b6b;font-size:11px;margin-top:2px}
.brand{color:#C9A84C;font-weight:bold;font-size:12px}
section.grp{margin-top:14px;page-break-inside:avoid}
section.grp h2{margin:0 0 6px 0;font-size:13px;letter-spacing:.5px;background:#f5f0dc;padding:6px 8px;border-left:3px solid #C9A84C}
table{width:100%;border-collapse:collapse;font-size:11px}
tbody tr:nth-child(even){background:#f5f5f5}
td{padding:6px 4px;vertical-align:middle}
.idx{color:#6b6b6b;width:38px}
.nums{font-weight:bold;letter-spacing:1px}
.num{display:inline-block;min-width:22px;text-align:center}
.cost{text-align:right;color:#6b6b6b;width:80px}
.total-lbl{text-align:right;font-weight:bold;padding-top:8px;border-top:1px solid #101829}
.total{font-weight:bold;color:#101829;padding-top:8px;border-top:1px solid #101829}
@media screen{body{background:#f5f5f5}main{max-width:780px;margin:20px auto;padding:14mm;background:white;box-shadow:0 0 0 1px #ddd}}
</style></head><body><main>
<header><div><h1>MEUS JOGOS</h1><div class="sub">${totalGames} jogo${totalGames !== 1 ? "s" : ""} • ${opts.groups.length} loteria(s)</div></div><div class="brand">${opts.brandLine ?? "LotoTerminal"}</div></header>
${sections}
</main><script>window.addEventListener('load',()=>setTimeout(()=>{window.focus();window.print();},120));</script></body></html>`;
}

// ============================================================
// MODO 2 — Volante oficial (apenas marcas pretas para impressão sobre o volante físico)
// ============================================================
export interface OfficialVolanteOptions {
  lotterySlug: "mega-sena" | "lotofacil" | "quina" | "dupla-sena";
  games: number[][];
  /** Deslocamento do volante físico dentro da página A4 virtual */
  pageOffsetX?: number;
  /** Ajuste fino em milímetros para alinhar à impressora */
  offsetX?: number;
  offsetY?: number;
}

export type OfficialVolanteLayout = {
  name: string;
  playsPerSlip: number;
  paperW: number; // largura do volante físico em mm
  paperH: number; // altura do volante físico em mm
  // Grade de dezenas
  cols: number;
  rows: number;
  min: number;
  /** X do canto sup-esq do retângulo da 1ª dezena (Aposta A), em mm */
  numFirstX: number;
  /** Y do canto sup-esq do retângulo da 1ª dezena (Aposta A), em mm */
  numFirstY: number;
  /** Distância centro-a-centro horizontal entre células = largura ret + espaçamento */
  numCellW: number;
  /** Distância centro-a-centro vertical entre células = altura ret + espaçamento */
  numCellH: number;
  numRectW: number;
  numRectH: number;
  /** Distância vertical (1ª dezena → 1ª dezena) entre Aposta A e B */
  playGapY: number;
  /** Mapeamento número → (col, row). Por padrão preenchimento por linhas. */
  mapNumberToCell?: (num: number, layout: OfficialVolanteLayout) => { col: number; row: number };
  // Grade de quantidade de dezenas marcadas no jogo
  qtyMin: number;
  qtyMax: number;
  qtyFirstX: number;
  qtyFirstY: number;
  qtyCellW: number;
  qtyRectW: number;
  qtyRectH: number;
};

/**
 * Layout oficial dos volantes da Caixa (em mm) conforme medidas fornecidas pelo usuário.
 * Papel: 83 mm (largura) × 186 mm (altura). Coordenadas medidas a partir do canto superior esquerdo,
 * referindo-se ao canto superior esquerdo do retângulo a ser preenchido.
 */
const lotofacilMap = (num: number) => {
  // Coluna 1 = 21..25, Coluna 2 = 16..20, ..., Coluna 5 = 01..05.
  const col = 4 - Math.floor((num - 1) / 5);
  const row = (num - 1) % 5;
  return { col, row };
};

export const OFFICIAL_VOLANTE_LAYOUTS: Record<OfficialVolanteOptions["lotterySlug"], OfficialVolanteLayout> = {
  "mega-sena": {
    name: "Mega Sena",
    playsPerSlip: 3,
    paperW: 83,
    paperH: 186,
    cols: 10, rows: 6, min: 1,
    numFirstX: 12, numFirstY: 48.5,
    numRectW: 4, numRectH: 2,
    numCellW: 4 + 2.4, numCellH: 2 + 1.2,
    playGapY: 25,
    qtyMin: 6, qtyMax: 20,
    qtyFirstX: 12, qtyFirstY: 125.5,
    qtyRectW: 4, qtyRectH: 2,
    qtyCellW: 4 + 2.4,
  },
  lotofacil: {
    name: "Lotofácil",
    playsPerSlip: 3,
    paperW: 83,
    paperH: 186,
    cols: 5, rows: 5, min: 1,
    numFirstX: 12, numFirstY: 46.5,
    numRectW: 4.5, numRectH: 3,
    numCellW: 4.5 + 8.1, numCellH: 3 + 1.6,
    playGapY: 25,
    mapNumberToCell: (n) => lotofacilMap(n),
    qtyMin: 15, qtyMax: 20,
    qtyFirstX: 12, qtyFirstY: 122.5,
    qtyRectW: 4, qtyRectH: 2,
    qtyCellW: 4 + 2.4,
  },
  quina: {
    name: "Quina",
    playsPerSlip: 3,
    paperW: 83,
    paperH: 186,
    cols: 10, rows: 8, min: 1,
    numFirstX: 12, numFirstY: 37.5,
    numRectW: 4, numRectH: 2,
    numCellW: 4 + 2.4, numCellH: 2 + 1.2,
    playGapY: 32,
    qtyMin: 5, qtyMax: 15,
    qtyFirstX: 12, qtyFirstY: 134.5,
    qtyRectW: 4, qtyRectH: 2,
    qtyCellW: 4 + 2.4,
  },
  "dupla-sena": {
    name: "Dupla Sena",
    playsPerSlip: 3,
    paperW: 83,
    paperH: 186,
    cols: 10, rows: 5, min: 1,
    numFirstX: 12, numFirstY: 53,
    numRectW: 4, numRectH: 2,
    numCellW: 4 + 2.4, numCellH: 2 + 1.2,
    playGapY: 23,
    qtyMin: 6, qtyMax: 15,
    qtyFirstX: 12, qtyFirstY: 123,
    qtyRectW: 4, qtyRectH: 2,
    qtyCellW: 4 + 2.4,
  },
};

function cellFor(layout: OfficialVolanteLayout, num: number) {
  if (layout.mapNumberToCell) return layout.mapNumberToCell(num, layout);
  const i = num - layout.min;
  return { col: i % layout.cols, row: Math.floor(i / layout.cols) };
}

export function getOfficialVolantePageCount(lotterySlug: OfficialVolanteOptions["lotterySlug"], gameCount: number) {
  const layout = OFFICIAL_VOLANTE_LAYOUTS[lotterySlug];
  return Math.max(1, Math.ceil(gameCount / layout.playsPerSlip));
}

export function generateOfficialVolantePDF(opts: OfficialVolanteOptions): jsPDF {
  const layout = OFFICIAL_VOLANTE_LAYOUTS[opts.lotterySlug];
  if (!layout) throw new Error("Layout de volante não disponível para esta loteria");
  const pageDx = opts.pageOffsetX ?? 0;
  const dx = opts.offsetX ?? 0;
  const dy = opts.offsetY ?? 0;

  const baseX = pageDx;
  const baseY = 0;
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  for (let page = 0; page < getOfficialVolantePageCount(opts.lotterySlug, opts.games.length); page++) {
    if (page > 0) doc.addPage();
    const pageGames = opts.games.slice(page * layout.playsPerSlip, (page + 1) * layout.playsPerSlip);
    doc.setFillColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);

    pageGames.forEach((game, playIdx) => {
      const playYBase = layout.numFirstY + playIdx * layout.playGapY;
      for (const num of game) {
        const { col, row } = cellFor(layout, num);
        const x = baseX + layout.numFirstX + col * layout.numCellW + dx;
        const y = baseY + playYBase + row * layout.numCellH + dy;
        doc.rect(x, y, layout.numRectW, layout.numRectH, "F");
      }
    });

    // Marcação da quantidade de dezenas — UMA ÚNICA VEZ por volante.
    // Usa a quantidade do primeiro jogo da página (todos os jogos do mesmo volante
    // devem ter a mesma quantidade de dezenas).
    const firstQty = pageGames[0]?.length ?? 0;
    if (firstQty >= layout.qtyMin && firstQty <= layout.qtyMax) {
      const qCol = firstQty - layout.qtyMin;
      const qx = baseX + layout.qtyFirstX + qCol * layout.qtyCellW + dx;
      const qy = baseY + layout.qtyFirstY + dy;
      doc.rect(qx, qy, layout.qtyRectW, layout.qtyRectH, "F");
    }
  }

  return doc;
}

export function buildOfficialVolantePrintHtml(opts: OfficialVolanteOptions): string {
  const layout = OFFICIAL_VOLANTE_LAYOUTS[opts.lotterySlug];
  if (!layout) throw new Error("Layout de volante não disponível para esta loteria");
  const pageDx = opts.pageOffsetX ?? 0;
  const dx = opts.offsetX ?? 0;
  const dy = opts.offsetY ?? 0;
  const pages = getOfficialVolantePageCount(opts.lotterySlug, opts.games.length);
  const sections: string[] = [];
  const pageW = 210;
  const pageH = 297;
  const baseX = 0;

  for (let page = 0; page < pages; page++) {
    const pageGames = opts.games.slice(page * layout.playsPerSlip, (page + 1) * layout.playsPerSlip);
    const marks: string[] = [];
    pageGames.forEach((game, playIdx) => {
      const playYBase = layout.numFirstY + playIdx * layout.playGapY;
      game.forEach((num) => {
        const { col, row } = cellFor(layout, num);
        const x = baseX + layout.numFirstX + col * layout.numCellW + dx;
        const y = playYBase + row * layout.numCellH + dy;
        marks.push(`<span class="mark" style="left:${x}mm;top:${y}mm;width:${layout.numRectW}mm;height:${layout.numRectH}mm"></span>`);
      });
    });
    const firstQty = pageGames[0]?.length ?? 0;
    if (firstQty >= layout.qtyMin && firstQty <= layout.qtyMax) {
      const qCol = firstQty - layout.qtyMin;
      const qx = baseX + layout.qtyFirstX + qCol * layout.qtyCellW + dx;
      const qy = layout.qtyFirstY + dy;
      marks.push(`<span class="mark" style="left:${qx}mm;top:${qy}mm;width:${layout.qtyRectW}mm;height:${layout.qtyRectH}mm"></span>`);
    }
    sections.push(`<section class="sheet"><div class="slip">${marks.join("")}</div></section>`);
  }

  return `<!doctype html><html><head><meta charset="utf-8" /><title>Impressão de volante</title><style>@page{size:A4 portrait;margin:0}html,body{margin:0;padding:0;background:white;width:${pageW}mm}.sheet{position:relative;width:${pageW}mm;height:${pageH}mm;margin:0;overflow:hidden}.sheet+.sheet{break-before:page;page-break-before:always}.slip{position:absolute;left:${pageDx}mm;top:0;width:${layout.paperW}mm;height:${layout.paperH}mm;overflow:hidden}.mark{position:absolute;display:block;background:#000;print-color-adjust:exact;-webkit-print-color-adjust:exact}@media screen{body{background:#f5f5f5;width:auto}.sheet{margin:12px auto;background:white;box-shadow:0 0 0 1px #ddd}.slip{box-shadow:inset 0 0 0 1px #ddd}}</style></head><body>${sections.join("")}<script>window.addEventListener('load',()=>setTimeout(()=>{window.focus();window.print();},120));</script></body></html>`;
}

/**
 * Gera um PDF inspirado nos volantes oficiais da loteria.
 * Layout: até 2 volantes por página A4, com cabeçalho, grade marcada e rodapé.
 * @deprecated Use generateGameListPDF (lista compacta) ou generateOfficialVolantePDF (impressão direta).
 */
export function generateVolantePDF(opts: VolanteOptions): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const volantesPerPage = 2;
  const volanteH = (pageH - margin * 2 - 8) / volantesPerPage;

  opts.games.forEach((game, idx) => {
    const slotIdx = idx % volantesPerPage;
    if (idx > 0 && slotIdx === 0) doc.addPage();
    const top = margin + slotIdx * (volanteH + 8);
    drawVolante(doc, opts, game, idx + 1, margin, top, pageW - margin * 2, volanteH);
  });

  return doc;
}

function drawVolante(
  doc: jsPDF,
  opts: VolanteOptions,
  game: number[],
  gameNum: number,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  // Borda externa
  doc.setDrawColor(...DARK);
  doc.setLineWidth(0.5);
  doc.rect(x, y, w, h);

  // Cabeçalho colorido
  const headerH = 14;
  doc.setFillColor(...DARK);
  doc.rect(x, y, w, headerH, "F");
  doc.setTextColor(...PRIMARY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(opts.lotteryName.toUpperCase(), x + 4, y + 6);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Volante ${String(gameNum).padStart(2, "0")} • ${opts.pickCount} dezenas`, x + 4, y + 11);
  doc.setTextColor(...PRIMARY);
  doc.setFontSize(7);
  doc.text(opts.brandLine ?? "LotoTerminal", x + w - 4, y + 11, { align: "right" });

  // Área da grade
  const gridTop = y + headerH + 4;
  const gridBottom = y + h - 10;
  const gridH = gridBottom - gridTop;
  const total = opts.universeMax - opts.universeMin + 1;
  const cols = opts.cols;
  const rows = Math.ceil(total / cols);

  const gridW = w - 8;
  const cellW = gridW / cols;
  const cellH = Math.min(cellW * 0.85, gridH / rows);
  const actualGridW = cellW * cols;
  const actualGridH = cellH * rows;
  const gridX = x + (w - actualGridW) / 2;
  const gridY = gridTop + (gridH - actualGridH) / 2;

  const picked = new Set(game);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  for (let i = 0; i < total; i++) {
    const num = opts.universeMin + i;
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = gridX + col * cellW + cellW / 2;
    const cy = gridY + row * cellH + cellH / 2;
    const r = Math.min(cellW, cellH) * 0.38;

    if (picked.has(num)) {
      // Marcado: círculo preenchido com cor primária
      doc.setFillColor(...PRIMARY);
      doc.setDrawColor(...DARK);
      doc.setLineWidth(0.3);
      doc.circle(cx, cy, r, "FD");
      doc.setTextColor(...DARK);
      doc.setFont("helvetica", "bold");
    } else {
      // Vazio: círculo só com borda
      doc.setDrawColor(...LIGHT_GRID);
      doc.setLineWidth(0.2);
      doc.circle(cx, cy, r, "S");
      doc.setTextColor(120, 120, 120);
      doc.setFont("helvetica", "normal");
    }

    doc.text(String(num).padStart(2, "0"), cx, cy + 1.2, { align: "center" });
  }

  // Rodapé do volante
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  const numbersStr = game.map((n) => String(n).padStart(2, "0")).join(" - ");
  doc.text(numbersStr, x + 4, y + h - 4);
  if (opts.costPerGame) {
    doc.text(`R$ ${opts.costPerGame.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, x + w - 4, y + h - 4, { align: "right" });
  }
}
